import React, { FC, useState } from 'react';
import classNames from 'classnames';
import { RatingType } from '../../../../types/Rating/RatingTypes';
import { ReactComponent as StarFilledIcon } from '../../../../assets/icons/ratingStarFilled.svg';
import { ReactComponent as StarOutlineIcon } from '../../../../assets/icons/ratingStarOutline.svg';
import { ReactComponent as HeartFilledIcon } from '../../../../assets/icons/ratingHeartFilled.svg';
import { ReactComponent as HeartOutlineIcon } from '../../../../assets/icons/ratingHeartOutline.svg';
import classes from './RatingWidget.module.scss';

type RatingWidgetProps = {
  ratingType: RatingType;
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
};

const values = [1, 2, 3, 4, 5] as const;

export const RatingWidget: FC<RatingWidgetProps> = ({
  ratingType,
  value,
  onChange,
  disabled
}) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const activeValue = hoverValue ?? value;

  const selectValue = (num: number) => {
    if (disabled) {
      return;
    }
    onChange(num);
  };

  const handleStarTouch = (num: number) => (event: React.TouchEvent<HTMLButtonElement>) => {
    event.preventDefault();
    selectValue(num);
  };

  if (ratingType === 'NUMBERS') {
    const fillWidth =
      activeValue === null
        ? '0%'
        : activeValue === 5
          ? '100%'
          : `${((activeValue - 0.5) / 5) * 100}%`;

    return (
      <div className={classNames(classes.root, classes.numbers)}>
        <div
          className={classes.numbersGrid}
          role="slider"
          aria-valuemin={1}
          aria-valuemax={5}
          aria-valuenow={value ?? undefined}
          onMouseLeave={() => setHoverValue(null)}
        >
          {values.map((num) => (
            <button
              key={`label-${num}`}
              type="button"
              className={classNames(
                classes.numberLabel,
                activeValue !== null && num <= activeValue && classes.numberLabelActive
              )}
              style={{ gridColumn: num, gridRow: 1 }}
              disabled={disabled}
              onClick={() => selectValue(num)}
              onMouseEnter={() => !disabled && setHoverValue(num)}
              aria-label={`Оценка ${num}`}
            >
              {num}
            </button>
          ))}
          <div className={classes.sliderTrack}>
            <div className={classes.sliderFill} style={{ width: fillWidth }} />
          </div>
          {values.map((num) => (
            <button
              key={`dot-${num}`}
              type="button"
              className={classNames(
                classes.sliderDot,
                activeValue !== null && num <= activeValue && classes.sliderDotActive
              )}
              style={{ gridColumn: num, gridRow: 2 }}
              disabled={disabled}
              onClick={() => selectValue(num)}
              onMouseEnter={() => !disabled && setHoverValue(num)}
              aria-hidden
              tabIndex={-1}
            />
          ))}
        </div>
      </div>
    );
  }

  const isHeart = ratingType === 'HEARTS';

  return (
    <div className={classes.root}>
      <div
        className={classes.icons}
        onMouseLeave={() => setHoverValue(null)}
      >
      {values.map((num) => {
        const isActive = activeValue !== null && num <= activeValue;
        const Icon = isHeart
          ? isActive
            ? HeartFilledIcon
            : HeartOutlineIcon
          : isActive
            ? StarFilledIcon
            : StarOutlineIcon;

        return (
          <button
            key={num}
            type="button"
            className={classNames(classes.iconButton, !isActive && classes.iconButtonInactive)}
            disabled={disabled}
            onClick={() => selectValue(num)}
            onTouchEnd={handleStarTouch(num)}
            onMouseEnter={() => !disabled && setHoverValue(num)}
            aria-label={`Оценка ${num}`}
          >
            <Icon className={classes.icon} aria-hidden />
          </button>
        );
      })}
      </div>
    </div>
  );
};
